import type { Franchise } from "./types.js";

/** The badge's colour when a franchise has chosen none. A neutral grey, readable under white. */
export const NEUTRAL_COLOUR = "#6b7280";

/**
 * What a franchise with no logo shows: its short name when it has one, otherwise the first
 * letters of up to two words of its name, upper-cased. Never empty for a franchise that has a
 * name, so a slot is never blank.
 */
export function initials(franchise: Pick<Franchise, "name" | "shortName">): string {
  const short = franchise.shortName?.trim();
  if (short) return short.toUpperCase();

  const words = franchise.name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

/**
 * The badge's background and a text colour that reads on it. White or near-black, picked by
 * perceived luminance, so an orange team and a yellow team are both legible.
 */
export function badgeColours(colour: string | null): { background: string; text: string } {
  const background = colour && /^#[0-9a-fA-F]{6}$/.test(colour) ? colour : NEUTRAL_COLOUR;

  const red = parseInt(background.slice(1, 3), 16);
  const green = parseInt(background.slice(3, 5), 16);
  const blue = parseInt(background.slice(5, 7), 16);
  const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;

  return { background, text: luminance > 0.6 ? "#111827" : "#ffffff" };
}
